/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: SpeedDialsPage.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Main page for Speed Dials management with list view.
    Displays speed dial entries for quick dialing.
────────────────────────────────────────────────────────────────*/

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, Download, Upload } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { useContextStore } from '../../store/context';
import { ModulePageContainer } from '../../components/layouts';
import DataGridSimple from '../../components/ui/DataGridSimple';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import { Button } from '../../components/ui/button';
import ImportSpeedDialsModal from './ImportSpeedDialsModal';
import { useSpeedDialsList } from './hooks/useSpeedDialsList';
import { useSpeedDialsColumns } from './hooks/useSpeedDialsColumns';
import SpeedDialsActions from './components/SpeedDialsActions';
import { deleteSpeedDial, exportSpeedDials } from './api/speedDialsApi';
import { navigateToSpeedDialDuplicate } from './speedDialsActions';
import type { SpeedDial } from './api/speedDialsApi';

export default function SpeedDialsPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotifications();
  const { selectedDomain } = useContextStore();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [speedDialToDelete, setSpeedDialToDelete] = useState<SpeedDial | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const {
    data,
    loading,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh
  } = useSpeedDialsList({
    domainId: selectedDomain?.id,
  });

  const handleCreateSpeedDial = useCallback(() => {
    navigate('/pbx/applications/speed-dialing/new');
  }, [navigate]);

  const handleEditSpeedDial = useCallback(
    (speedDial: SpeedDial) => {
      navigate(`/pbx/applications/speed-dialing/${speedDial.uuid || speedDial.id}`);
    },
    [navigate]
  );

  const handleDuplicateSpeedDial = useCallback(
    (speedDial: SpeedDial) => {
      navigateToSpeedDialDuplicate(navigate, speedDial.uuid || speedDial.id);
    },
    [navigate]
  );

  const handleDeleteSpeedDial = useCallback((speedDial: SpeedDial) => {
    setSpeedDialToDelete(speedDial);
    setShowDeleteModal(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!speedDialToDelete?.id) return;

    setIsDeleting(true);
    try {
      await deleteSpeedDial(String(speedDialToDelete.uuid || speedDialToDelete.id));
      showSuccess(t('speedDials.messages.deleteSuccess'));
      setShowDeleteModal(false);
      setSpeedDialToDelete(null);
      refresh();
    } catch (error) {
      showError(error?.message || t('speedDials.messages.deleteError'));
    } finally {
      setIsDeleting(false);
    }
  }, [speedDialToDelete, refresh, showSuccess, showError, t]);

  const handleExport = useCallback(async () => {
    setIsExporting(true);
    try {
      const blob = await exportSpeedDials();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `speed-dials-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      showSuccess(t('speedDials.messages.exportSuccess'));
    } catch (error) {
      showError(t('speedDials.messages.exportError'));
    } finally {
      setIsExporting(false);
    }
  }, [showError, showSuccess, t]);

  const handleImport = useCallback(() => {
    setImportModalOpen(true);
  }, []);

  const handleImportSuccess = useCallback(() => {
    setImportModalOpen(false);
    showSuccess(t('speedDials.messages.importSuccess'));
    refresh();
  }, [refresh, showSuccess, t]);

  const renderActions = useCallback(
    (item: SpeedDial) => (
      <SpeedDialsActions
        item={item}
        onEdit={handleEditSpeedDial}
        onDuplicate={handleDuplicateSpeedDial}
        onDelete={handleDeleteSpeedDial}
      />
    ),
    [handleEditSpeedDial, handleDuplicateSpeedDial, handleDeleteSpeedDial]
  );

  const { columns } = useSpeedDialsColumns(renderActions);

  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <DataGridSimple
          data={data}
          columns={columns}
          onColumnToggle={handleColumnToggle}
          loading={loading}
          pagination={pagination}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          searchQuery={searchQuery}
          onSearch={handleSearch}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
          onAdd={handleCreateSpeedDial}
          onEdit={handleEditSpeedDial}
          onDuplicate={handleDuplicateSpeedDial}
          onDelete={handleDeleteSpeedDial}
          title={t('speedDials.title')}
          titleIcon={<Phone className="h-5 w-5 text-primary" />}
          addButtonText={t('common.add')}
          searchPlaceholder={t('common.searchPlaceholder')}
          emptyMessage={t('speedDials.emptyMessage')}
          renderActions={renderActions}
          actions={
            <div className="flex items-center gap-2">
              <SimpleTooltip content={t('speedDials.actions.export')} position="top">
                <Button
                  onClick={handleExport}
                  disabled={isExporting}
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-2"
                >
                  <Download className="h-4 w-4" />
                  <span className="hidden sm:inline">{t('speedDials.actions.export')}</span>
                </Button>
              </SimpleTooltip>
              <SimpleTooltip content={t('speedDials.actions.import')} position="top">
                <Button
                  onClick={handleImport}
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-2"
                >
                  <Upload className="h-4 w-4" />
                  <span className="hidden sm:inline">{t('speedDials.actions.import')}</span>
                </Button>
              </SimpleTooltip>
            </div>
          }
          translations={{
            items: t('common.items'),
            columns: t('common.columns'),
            noItemsFound: t('common.noItemsFound'),
            showing: t('common.showing'),
            to: t('common.to'),
            of: t('common.of'),
            results: t('common.results'),
            previous: t('common.previous'),
            next: t('common.next'),
            page: t('common.page'),
            deleteItem: t('common.deleteItem'),
            areYouSureDeleteItem: t('common.areYouSureDeleteItem'),
            delete: t('common.delete'),
            cancel: t('common.cancel'),
            allSelected: t('common.allSelected'),
            selected: t('common.selected'),
            searchOptions: t('common.searchOptions'),
            noOptionsFound: t('common.noOptionsFound'),
            selectAll: t('common.selectAll'),
            clearAll: t('common.clearAll')
          }}
        />
      }
      formView={null}
    >
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSpeedDialToDelete(null);
        }}
        onConfirm={confirmDelete}
        title={t('speedDials.actions.delete')}
        message={
          speedDialToDelete
            ? t('speedDials.messages.deleteConfirm', {
              name: speedDialToDelete.speedCode || 'N/A'
            })
            : ''
        }
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        isLoading={isDeleting}
        type="danger"
      />

      <ImportSpeedDialsModal
        isOpen={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onSuccess={handleImportSuccess}
      />
    </ModulePageContainer>
  );
}
